import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum IncidentSeverity {
  INFO = "info",
  WARNING = "warning",
  ERROR = "error",
}

export enum IncidentStatus {
  ACTIVE = "active",
  RESOLVED = "resolved",
}

export enum IncidentProduct {
  OMS = "oms",
  ALL = "all",
  MARKETING = "marketing",
  RETURNS = "returns",
  CONVERSION = "conversion",
  RECOMMERCE = "recommerce",
  SUPPORT = "support",
}

export const IncidentSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.id.internal }),
  title: z.string().meta({ privacy: privacy.text.redoInternal }),
  message: z.string().meta({ privacy: privacy.text.redoInternal }),
  severity: createEnum(IncidentSeverity, { enumName: "IncidentSeverity" }).meta(
    { privacy: privacy.enumOrStatus },
  ),
  status: createEnum(IncidentStatus, { enumName: "IncidentStatus" }).meta({
    privacy: privacy.enumOrStatus,
  }),
  type: createEnum(IncidentProduct, { enumName: "IncidentProduct" }).meta({
    privacy: privacy.enumOrStatus,
  }),
  types: z
    .array(
      createEnum(IncidentProduct, { enumName: "IncidentProduct" }).meta({
        privacy: privacy.enumOrStatus,
      }),
    )
    .optional(),
  affectedTeams: z
    .array(zExt.objectId().meta({ privacy: privacy.id.internal }))
    .nullish()
    .meta({ privacy: privacy.id.internal }),
  isGlobal: z.boolean().meta({ privacy: privacy.flag }),
  isTest: z.boolean().meta({ privacy: privacy.flag }),
  adminOnly: z.boolean().optional().meta({ privacy: privacy.flag }),
  routeScoped: z.boolean().optional().meta({ privacy: privacy.flag }),
  createdAt: z.date().meta({ privacy: privacy.timestamp }),
  resolvedAt: z.date().nullish().meta({ privacy: privacy.timestamp }),
  updatedAt: z.date().meta({ privacy: privacy.timestamp }),
  createdBy: z
    .string()
    .nullish()
    .meta({ privacy: privacy.person_name.redoInternal }),
  notes: z.string().nullish().meta({ privacy: privacy.text.redoInternal }),
  publicUrl: z.string().nullish().meta({ privacy: privacy.publicUrl }),
});
export type Incident = z.infer<typeof IncidentSchema>;
export interface IIncident extends z.ZodType<Incident> {}

const severityOrder = [
  IncidentSeverity.ERROR,
  IncidentSeverity.WARNING,
  IncidentSeverity.INFO,
];

export const incidentSort: (a: Incident, b: Incident) => number = (a, b) => {
  // severity desc, createdAt desc
  if (a.severity !== b.severity) {
    return (
      severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity)
    );
  }
  return b.createdAt.getTime() - a.createdAt.getTime();
};
